import { useEffect, useRef } from "react"

import { SCAN_PREFIX_SUFFIX } from "lib/constants"

const keysToIgnore = [
  "Shift",
  "Alt",
  "Control",
  "Backspace",
  "ArrowRight",
  "ArrowLeft",
  "ArrowUp",
  "ArrowDown",
]

/** `Element.closest` throws on invalid selector strings; treat as no match. */
function elementClosestSafe(
  element: Element,
  selector: string
): Element | null {
  try {
    return element.closest(selector)
  } catch {
    return null
  }
}

/** Pipe wedges only target text fields; skip false positives on buttons etc. */
function isTextField(
  el: EventTarget | null
): el is HTMLInputElement | HTMLTextAreaElement {
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
}

function shouldBypassPipeScanForActiveElement(
  activeElement: EventTarget | null,
  ignorePipeScanWhenFocusWithin: string
): boolean {
  if (!isTextField(activeElement)) {
    return false
  }
  return (
    elementClosestSafe(activeElement, ignorePipeScanWhenFocusWithin) !== null
  )
}

// useBarcodeScanner is a hook that captures barcode scans and calls the
// provided callback with the text representation of the barcode.
//
// With most barcode scanners, their input is indistinguishable from keyboard input.
// As such, we detect barcode scans by listening for keydown events. We look for keyboard
// entry prefixed & suffixed with the pipe character ("|").
export function useBarcodeScanner({
  onScan,
  preventScanIntoInputs,
  disabled,
  /** When set, pipe-delimited scan handling is skipped for key events whose target is an input/textarea inside this selector (e.g. Fan Builder coupon field). Invalid selectors are ignored. */
  ignorePipeScanWhenFocusWithin,
}: {
  onScan: (barcodeText: string) => void
  preventScanIntoInputs?: boolean
  disabled?: boolean
  ignorePipeScanWhenFocusWithin?: string
}) {
  const buffer = useRef<Array<string>>([])

  useEffect(() => {
    if (disabled) return
    function onKeyDown(event: KeyboardEvent) {
      if (
        ignorePipeScanWhenFocusWithin &&
        shouldBypassPipeScanForActiveElement(
          document.activeElement,
          ignorePipeScanWhenFocusWithin
        )
      ) {
        buffer.current = []
        return
      }

      if (keysToIgnore.includes(event.key)) {
        return
      }

      const isPrefix =
        event.key === SCAN_PREFIX_SUFFIX && buffer.current.length === 0
      const isMiddle = buffer.current.length > 0
      const isSuffix =
        event.key === SCAN_PREFIX_SUFFIX && buffer.current.length > 0

      // always prevent the prefix & suffix from populating inputs;
      // conditionally prevent the barcode from populating inputs
      if (isPrefix || isSuffix || (isMiddle && preventScanIntoInputs)) {
        event.preventDefault()
      }

      if (isSuffix) {
        const code = buffer.current
          .filter((x) => x !== SCAN_PREFIX_SUFFIX)
          .join("")
        onScan(code)
        buffer.current = []
        return
      }

      if (isPrefix || isMiddle) {
        buffer.current.push(event.key)
      }
    }

    document.addEventListener("keydown", onKeyDown)

    return () => {
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [onScan, preventScanIntoInputs, disabled, ignorePipeScanWhenFocusWithin])
}
